import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Plan {
  name: string;
  price: number;
  original_price?: number;
  setup_fee?: string;
  features: string[] | { [key: string]: any };
}

interface UpdatePlansParams {
  productId: string;
  plans: Plan[];
}

export const useProductPlans = () => {
  const queryClient = useQueryClient();

  const updatePlans = useMutation({
    mutationFn: async ({ productId, plans }: UpdatePlansParams) => {
      // First, get the product's database ID
      const { data: product } = await supabase
        .from('products')
        .select('id')
        .eq('slug', productId)
        .single();

      if (!product) {
        throw new Error('Product not found');
      }

      // Delete all existing plans for this product
      await supabase
        .from('product_plans')
        .delete()
        .eq('product_id', product.id);

      // Insert new plans
      if (plans.length > 0) {
        const plansData = plans.map((plan, index) => ({
          product_id: product.id,
          name: plan.name,
          price: plan.price,
          original_price: plan.original_price,
          setup_fee: plan.setup_fee,
          features: plan.features,
          sort_order: index,
        }));

        const { error } = await supabase.from('product_plans').insert(plansData);

        if (error) throw error;
      }

      return { success: true };
    },
    onSuccess: async (_, variables) => {
      await queryClient.refetchQueries({ 
        queryKey: ['product', variables.productId],
        exact: false 
      });
      toast.success('Plans updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating plans:', error);
      toast.error('Failed to update plans: ' + error.message);
    },
  });

  return {
    updatePlans: updatePlans.mutate,
    isUpdating: updatePlans.isPending,
  };
};
